/**
 * Lane SCENE-PLAINS, BUILDINGS-R1 "missing buildings" (docs/review/art/BUILDINGS-R1.md): a thatch cottage, a dark-timber
 * two-storey townhouse, a moon gate between tiled-coping walls, a log watchtower, a log camp gate and a drum tower.
 * Painters keyed by the layout's Thai name (kit/index.ts); the footprints are the layout's (collision in plainsMap.ts).
 */
import { hash2, valueNoise } from '@nine/shared';
import { box, door, M, snap, wall, type Ramp } from '../buildings';
import type { Sprite } from '../scenery';
import { boardWalls, faces, hipRoof, plinthK, timber, windows, type Rect } from './common';
import { column, done, finial, K, rail, ROOF_TILES, stage, type RoofColour } from './parts';
import { plaque } from '../buildings';

const logTex = (mat: Ramp = M.wood) => (u: number, v: number, l: number) =>
  snap(mat, l + (Math.floor(v / 5) % 2 ? 0.06 : -0.06) + (v % 5 < 1 ? -0.25 : 0) + (valueNoise(u / 5, v / 3) - 0.5) * 0.12, u | 0, v | 0, 0.2);

/** กระท่อมมุงจาก: a board hut under a deep thatched hip roof (w × h tiles, ≈ 100 px). */
export function thatchCottage(w: number, h: number, v: number): Sprite {
  const s = stage(w, h, 108);
  const r: Rect = [0.6, 0.6, w - 0.6, h - 0.9];
  plinthK(s, [0.3, 0.3, w - 0.3, h - 0.3], 4, false);
  boardWalls(s, r, 4, 44, v % 2 ? M.wood : M.deck, 50);
  timber(s, 'left', r[3], r[0], r[2], 4, 44, 1.5, [42], M.timber, 50);
  timber(s, 'right', r[2], r[1], r[3], 4, 44, 1.5, [42], M.timber, 50);
  door(s.px, s.P, 'left', r[3], w / 2 - 0.5, w / 2 + 0.5, 4, 34, M.timber);
  windows(s, 'right', r[2], r[1] + 0.6, r[3] - 0.6, 18, 34, 1, 0.35, 50);
  hipRoof(s, [-0.1, -0.1, w + 0.1, h - 0.2], 44, [w / 2 - 0.6, h / 2 - 0.2, w / 2 + 0.6, h / 2 - 0.2], 92, ROOF_TILES.wood, { horns: 0, shadow: 14 });
  return done(s);
}

/** เรือนไม้สองชั้น: a dark-timber townhouse, a balcony on the upper storey, a charcoal hip roof. */
export function timberTownhouse(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 156);
  const r: Rect = [0.4, 0.4, w - 0.4, h - 0.8];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 6);
  const z1 = 62;
  const eave = 110;
  boardWalls(s, r, 6, z1, M.wood, z1 + 8);
  timber(s, 'left', r[3], r[0], r[2], 6, z1, 1.3, [z1 - 3], M.timber, z1 + 8);
  timber(s, 'right', r[2], r[1], r[3], 6, z1, 1.3, [z1 - 3], M.timber, z1 + 8);
  door(s.px, s.P, 'left', r[3], w / 2 - 0.6, w / 2 + 0.6, 6, 48, M.timber);
  windows(s, 'right', r[2], r[1], r[3], 20, 46, 3, 0.45, z1 + 8);
  hipRoof(s, [r[0] - 0.5, r[1] - 0.5, r[2] + 0.5, r[3] + 0.5], z1, [r[0], r[1], r[2], r[3]], z1 + 8, ROOF_TILES[colour], { ridge: false, horns: 0.5 });
  const u: Rect = [0.7, 0.7, w - 0.7, h - 1.1];
  boardWalls(s, u, z1 + 8, eave, M.wood, eave);
  timber(s, 'left', u[3], u[0], u[2], z1 + 8, eave, 1.0, [eave - 4], M.timber, eave);
  timber(s, 'right', u[2], u[1], u[3], z1 + 8, eave, 1.0, [eave - 4], M.timber, eave);
  windows(s, 'left', u[3], u[0], u[2], z1 + 16, eave - 12, 3, 0.55, eave);
  windows(s, 'right', u[2], u[1], u[3], z1 + 16, eave - 12, 3, 0.55, eave);
  rail(s.px, s.P, 'left', u[3] + 0.3, u[0] - 0.2, u[2] + 0.2, z1 + 8, 11, K.maroon);
  rail(s.px, s.P, 'right', u[2] + 0.3, u[1] - 0.2, u[3] + 0.2, z1 + 8, 11, K.maroon);
  hipRoof(s, [0.0, 0.0, w, h - 0.3], eave, [1.4, h / 2 - 0.2, w - 1.4, h / 2 - 0.2], 148, ROOF_TILES[colour], { horns: 1 });
  return done(s);
}

/** ประตูพระจันทร์: a plaster wall with a round opening and tiled coping (L × 1, L ≥ 4; the pillars are the end tiles). */
export function moonGate(L: number, colour: RoofColour): Sprite {
  const s = stage(L, 1, 90, 24);
  const H = 54;
  const cx = (L / 2) * 16;
  const R = Math.min(18, (L / 2 - 0.6) * 16);
  box(s.px, s.P, [0, 0.25, L, 0.75], 0, H, (u, v, l) => {
    const d = Math.hypot(u - cx, v - 26);
    if (d < R) return null;
    if (d < R + 3) return snap(M.stone, l + 0.2, u | 0, v | 0, 0);
    return snap(K.wallPlaster, l - 0.1 - (v < 8 ? 0.15 : 0) + (valueNoise(u / 7, v / 9) - 0.5) * 0.12, u | 0, v | 0);
  }, (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0.2));
  hipRoof(s, [-0.15, 0.1, L + 0.15, 0.9], H, [0.4, 0.5, L - 0.4, 0.5], H + 9, ROOF_TILES[colour], { horns: 0.6, shadow: 6 });
  return done(s);
}

/** หอสังเกตการณ์ไม้: a four-post log watchtower with a plank deck, a rail and a small roof (w × h, ≈ 150 px). */
export function logWatchtower(w: number, h: number): Sprite {
  const s = stage(w, h, 150, 26);
  const z0 = 62;
  for (const [x, y] of [[0.5, 0.5], [w - 0.5, 0.5], [0.5, h - 0.5], [w - 0.5, h - 0.5]] as const)
    box(s.px, s.P, [x - 0.2, y - 0.2, x + 0.2, y + 0.2], 0, z0, logTex(), logTex());
  for (const z of [16, 38]) {
    faces(s, [0.3, 0.3, w - 0.3, h - 0.3], z, z + 3, (u, v, l) => snap(M.wood, l - 0.15, u | 0, v | 0, 0));
  }
  box(s.px, s.P, [0.1, 0.1, w - 0.1, h - 0.1], z0 - 3, z0 + 1, (u, v, l) => snap(M.deck, l, u | 0, v | 0), (u, v, l) => snap(M.deck, l + 0.1, u | 0, v | 0));
  rail(s.px, s.P, 'left', h - 0.15, 0.15, w - 0.15, z0 + 1, 14, M.wood, 4);
  rail(s.px, s.P, 'right', w - 0.15, 0.15, h - 0.15, z0 + 1, 14, M.wood, 4);
  for (const [x, y] of [[0.5, 0.5], [w - 0.5, 0.5], [0.5, h - 0.5], [w - 0.5, h - 0.5]] as const)
    box(s.px, s.P, [x - 0.12, y - 0.12, x + 0.12, y + 0.12], z0 + 1, 110, logTex(), logTex());
  hipRoof(s, [-0.2, -0.2, w + 0.2, h + 0.2], 108, [w / 2, h / 2, w / 2, h / 2], 134, ROOF_TILES.wood, { horns: 0.6, shadow: 12 });
  return done(s);
}

/** ประตูค่ายโจร: two log posts under a plank lintel with a banner board (L × 1; the posts are the end tiles). */
export function campGate(L: number): Sprite {
  const s = stage(L, 1, 92, 20);
  const H = 64;
  for (const x of [0.5, L - 0.5]) {
    box(s.px, s.P, [x - 0.3, 0.2, x + 0.3, 0.8], 0, H, logTex(), logTex());
    box(s.px, s.P, [x - 0.36, 0.14, x + 0.36, 0.86], H - 3, H + 3, (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0), (u, v, l) => snap(M.stone, l + 0.1, u | 0, v | 0, 0));
  }
  box(s.px, s.P, [0.2, 0.3, L - 0.2, 0.7], H - 12, H - 2, logTex(M.deck), logTex(M.deck));
  plaque(s.px, s.P, 'left', 0.74, L / 2 - 0.9, L / 2 + 0.9, H - 24, H - 10, M.dark);
  box(s.px, s.P, [-0.1, 0.1, L + 0.1, 0.9], H + 3, H + 8, (u, v, l) => snap(M.wood, l - 0.1, u | 0, v | 0, 0), (u, v, l) => snap(M.wood, l, u | 0, v | 0, 0));
  return done(s);
}

/** หอกลอง: a stone terrace with a vaulted door, an open timber storey with a great drum, a double hip roof. */
export function drumTower(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 200, 30);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.4, 0.4, w - 0.4, h - 0.4];
  faces(s, r, 0, 52, (u, v, l) => {
    const row = Math.floor(v / 4);
    const off = row & 1 ? 4 : 0;
    if (v % 4 < 1 || (u + off) % 8 < 1) return snap(M.stone, l - 0.1, u | 0, v | 0, 0);
    return snap(M.brick, l + (hash2(Math.floor((u + off) / 8) * 7, row) - 0.5) * 0.22, u | 0, v | 0);
  });
  wall(s.px, s.P, 'left', r[3], w / 2 - 0.6, w / 2 + 0.6, 0, 36, (u, v) => {
    const c = (w / 2) * 16;
    return v < 24 + Math.sqrt(Math.max(0, 1 - ((u - c) / 9.6) ** 2)) * 8 ? snap(M.dark, 0.2, u | 0, v | 0) : null;
  });
  hipRoof(s, [0.0, 0.0, w, h], 52, [0.5, 0.5, w - 0.5, h - 0.5], 58, tiles, { ridge: false, horns: 0.6 });
  const z0 = 58;
  const z1 = 122;
  for (const [x, y] of [[0.9, 0.9], [w - 0.9, 0.9], [0.9, h - 0.9], [w - 0.9, h - 0.9]] as const) column(s.px, s.P, x, y, 0.11, z0, z1, K.maroon);
  rail(s.px, s.P, 'left', h - 0.8, 0.8, w - 0.8, z0, 12, K.maroon);
  rail(s.px, s.P, 'right', w - 0.8, 0.8, h - 0.8, z0, 12, K.maroon);
  const [dx, dy] = s.P(w / 2, h / 2, z0 + 34);
  for (let y = -17; y <= 17; y++) {
    const half = Math.round(Math.sqrt(Math.max(0, 1 - (y / 17.5) ** 2)) * 17);
    for (let a = -half; a <= half; a++) s.px.set(dx + a, dy + y, snap(K.maroon, 0.45 + (a / 17) * 0.3 - (y / 17) * 0.1, a, y, 0.2));
  }
  for (let y = -17; y <= 17; y++) s.px.set(dx - 17 + (y % 5 === 0 ? 0 : 1), dy + y, M.gold[3]!);
  hipRoof(s, [-0.1, -0.1, w + 0.1, h + 0.1], z1, [w / 2, h / 2, w / 2, h / 2], 170, tiles, { horns: 1.1, tassels: true });
  finial(s.px, s.P(w / 2, h / 2, 170), 8);
  return done(s);
}
